import { convertString } from 'converter';
import { FieldProps, Field } from 'formik';
import React, { useCallback } from 'react';
import {
  FormPageInputItemMonthDay,
  MonthDayProperty,
  FormPageInputData,
  StringConvertRule,
} from 'shared';
import { FormFieldProps } from '../shared';
import { useFieldPropsHandler } from '../useFieldPropsHandler';
import { MonthDayInput, MonthDayInputProps } from './MonthDayInput';

type Props = FormFieldProps<MonthDayProperty>;

const Container: React.FC<
  Props & {
    fieldProps: FieldProps<FormPageInputItemMonthDay, FormPageInputData>;
  }
> = ({ fieldProps, property, design, disabled, submitted }) => {
  const { propertyId, stringConvertRule } = property;
  const {
    fieldTouched,
    fieldErrorMessages,
    updateFieldValue,
    updateFieldTouched,
  } = useFieldPropsHandler<FormPageInputItemMonthDay>({
    fieldProps,
    propertyId,
  });

  const handleChange: MonthDayInputProps['onChange'] = useCallback(
    (key, updateValue = '') => {
      updateFieldValue(key, updateValue);
    },
    [updateFieldValue]
  );
  const handleBlur: MonthDayInputProps['onBlur'] = useCallback(
    (key, updateValue = '') => {
      const convertedValue = convertInputMonthDayValue(
        stringConvertRule,
        updateValue
      );
      updateFieldValue(key, convertedValue);
      updateFieldTouched(key);
    },
    [updateFieldValue, updateFieldTouched, stringConvertRule]
  );

  if (fieldProps.field.value == null) {
    return null;
  }

  return (
    <MonthDayInput
      property={property}
      design={design}
      disabled={disabled}
      value={fieldProps.field.value}
      onChange={handleChange}
      onBlur={handleBlur}
      errorMessages={fieldErrorMessages}
      touched={fieldTouched}
      submitted={submitted}
    />
  );
};

export const MonthDay: React.FC<Props> = (props) => {
  return (
    <Field name={props.property.propertyId}>
      {(fieldProps: FieldProps<FormPageInputItemMonthDay>) => (
        <Container {...props} fieldProps={fieldProps} />
      )}
    </Field>
  );
};

export const convertInputMonthDayValue = (
  stringConvertRule: StringConvertRule | undefined,
  updateValue: string | undefined = ''
): string => {
  if (stringConvertRule == null) {
    return updateValue;
  }
  const convertValue = convertString(stringConvertRule);
  const convertedValue = convertValue(updateValue);
  return convertedValue;
};
